import { useCallback, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as R from 'ramda';
import cn from 'classnames';
import { useQueryClient } from '@tanstack/react-query';
import { TYPES_TO_NAMES } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { Placement } from '@Components/designConstants';
import { FilterBar } from '@Components/FilterBar';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { PendingChangesControls } from '@Components/PendingChanges/PendingChangesControls';
import { SelfFocusingInput } from '@Components/SelfFocusingElements';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { TableLoading } from '@Components/TableEmptyState/TableLoading';
import { useSetError } from '@src/redux/modals';
import { convert24hrTo12hr } from '@src/utils/time-utils';
import { PacingBar } from '@Pages/Pacing/components/PacingBar';
import { PacingPane } from '@Pages/Pacing/components/PacingPane';
import { PacingBarColors } from '@src/utils/colors';
import {
  deliveryLayers,
  pacingKpis,
  pacingLabel,
  reprocessingLayers,
  NO_COMMISSION_OR_PRELOGS_WARNING,
  NO_PRELOGS_WARNING
} from '../components/bar-layers';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { CompanyLabel, NetworkLabel } from '../components/PacingLabels';
import { PacingOverlay } from '../components/PacingOverlay';
import { LoadFailedState, RowsSkeleton } from '../components/PacingSkeletons';
import { totalsRenderer } from '../components/totals-renderer';
import {
  ACCEPTED_LABEL,
  AcceptedDimension,
  COMPANY_DIMENSION,
  COMPANY_LABEL,
  MediaTypeDimension,
  NETWORK_DIMENSION,
  NETWORK_LABEL,
  PrimaryDimension,
  SpendDimension
} from '../linear-pacing-constants';
import {
  calculateAdjustedSpend,
  displayedSpend,
  hasSpendOverride,
  paneTitle,
  uniqueLines
} from '../linear-pacing-utils';
import {
  BottomDataRow,
  LOGS_QUERY_KEY,
  saveEstimateOverrides,
  SUMMARY_QUERY_KEY,
  SummaryRow,
  useCompanyNetworkLogs,
  useLinearPacingSummary
} from './use-linear-pacing-campaign';

// A summary row plus the name (company or network) it's shown under and its pane key. Company
// rows are keyed `company_campaign` when they have a campaign.
type PacingRow = SummaryRow & { name: string; key: string };

type RowFilter = (row: object) => boolean;

// Bottom table rows; the totals row puts prefixed strings ('$123', '#4') in the numeric columns.
type BottomTableRow = Omit<BottomDataRow, 'count' | 'spend'> & {
  count: number | string;
  spend: number | string;
};

const toPacingRows = (rows: SummaryRow[], dimension: PrimaryDimension): PacingRow[] =>
  rows.flatMap(row => {
    const name = row[dimension];
    if (typeof name !== 'string') {
      return [];
    }
    // Readable, since the page shows the selected keys as breadcrumbs.
    const key =
      dimension === COMPANY_DIMENSION && row.campaign ? `${name} · ${row.campaign}` : name;
    return [{ ...row, name, key }];
  });

const estimatedSpend = (row: SummaryRow, acceptedDimension: AcceptedDimension): number =>
  acceptedDimension === ACCEPTED_LABEL ? row.estimated.spend : row.estimatedAll.spend;

const maxSpend = (rows: PacingRow[], acceptedDimension: AcceptedDimension): number =>
  Math.max(
    0,
    ...rows.map(row => Math.max(row.booked.spend, estimatedSpend(row, acceptedDimension)))
  );

const adjustedSpends = (
  row: SummaryRow,
  spendDimension: SpendDimension,
  acceptedDimension: AcceptedDimension
) => {
  const isAccepted = acceptedDimension === ACCEPTED_LABEL;
  const adjust = (spend: number) => calculateAdjustedSpend(spend, row.commission, spendDimension);
  return {
    estimated: adjust(estimatedSpend(row, acceptedDimension)),
    logs: adjust(isAccepted ? row.logs.spend : row.logsAll.spend),
    unsecuredLogs: adjust(isAccepted ? row.unsecuredLogs.spend : row.unsecuredLogsAll.spend),
    securedLogs: adjust(isAccepted ? row.securedLogs.spend : row.securedLogsAll.spend),
    upfrontLogs: adjust(isAccepted ? row.upfrontLogs.spend : row.upfrontLogsAll.spend),
    booked: adjust(row.booked.spend),
    budget: row.tvBudget && adjust(row.tvBudget)
  };
};

type AdjustedSpends = ReturnType<typeof adjustedSpends>;

const roundCents = (spend: number) => Math.round(spend * 100) / 100;

// A pane's rows as shown: the Gross/Net and Accepted/All toggles applied, in display order.
const paneCsvRows = (
  rows: PacingRow[],
  dimension: PrimaryDimension,
  spendDimension: SpendDimension,
  acceptedDimension: AcceptedDimension
): unknown[][] => [
  [
    dimension === COMPANY_DIMENSION ? COMPANY_LABEL : NETWORK_LABEL,
    'Campaign',
    'Booked',
    'On the Logs (Unsecured)',
    'On the Logs (Secured)',
    'On the Logs (Upfront)',
    'On the Logs',
    'Pacing',
    'Pacing vs Booked'
  ],
  ...rows.map(row => {
    const spends = adjustedSpends(row, spendDimension, acceptedDimension);
    return [
      row.name,
      row.campaign ?? '',
      ...[
        spends.booked,
        spends.unsecuredLogs,
        spends.securedLogs,
        spends.upfrontLogs,
        spends.logs,
        spends.estimated
      ].map(roundCents),
      spends.booked ? `${Math.round((spends.estimated / spends.booked) * 100)}%` : ''
    ];
  })
];

const spendLayers = (spends: AdjustedSpends) =>
  deliveryLayers({
    secured: spends.securedLogs,
    unsecured: spends.unsecuredLogs,
    upfront: spends.upfrontLogs,
    estimated: spends.estimated
  });

const sumSpotCost = (row: BottomDataRow): number =>
  row.spots.reduce((total, spot) => total + spot.cost, 0);

const DAY_COLUMNS = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'] as const;

const createLogsHeaderRow = (dow: string, index: number): TypedHeader<BottomDataRow> => ({
  label: dow,
  name: dow,
  contentGetter: row => row.spots[index].cost,
  renderer: row => (
    <div className={cn({ 'pacing-spend-override': hasSpendOverride(row.spend_override) })}>
      <NumberFormatter value={row.spots[index].cost} type='$' decimals={0} />
    </div>
  ),
  width: 70
});

const BOTTOM_SUPER_HEADERS = [
  { span: 7, data: 'Summary' },
  { span: 3, data: 'Ordered' },
  { span: 9, data: 'Logs' }
];

const renderLabel = (dimension: PrimaryDimension, { name, campaign }: PacingRow) =>
  dimension === COMPANY_DIMENSION ? (
    <CompanyLabel company={name} campaign={campaign} />
  ) : (
    <NetworkLabel network={name} />
  );

interface LinearPacingDetail2Props {
  spendDimension: SpendDimension;
  acceptedDimension: AcceptedDimension;
  mediaTypeDimension: MediaTypeDimension;
  leftKey: PrimaryDimension;
  start: string;
  // Owned by the page, which shows them as breadcrumbs.
  selectedLeft?: string;
  setSelectedLeft: (key?: string) => void;
  selectedRight?: string;
  setSelectedRight: (key?: string) => void;
}

export const LinearPacingDetail2 = ({
  spendDimension,
  acceptedDimension,
  mediaTypeDimension,
  leftKey,
  start,
  selectedLeft,
  setSelectedLeft,
  selectedRight,
  setSelectedRight
}: LinearPacingDetail2Props) => {
  const rightKey = leftKey === COMPANY_DIMENSION ? NETWORK_DIMENSION : COMPANY_DIMENSION;
  const [leftFilter, setLeftFilter] = useState<RowFilter>(() => () => true);
  const [rightFilter, setRightFilter] = useState<RowFilter>(() => () => true);
  const [filteredBottomData, setFilteredBottomData] = useState<BottomDataRow[]>();
  const [spendOverrides, setSpendOverrides] = useState<Record<number, number>>({});
  const [focusedCell, setFocusedCell] = useState<number>();
  const [saving, setSaving] = useState(false);

  const setError = useSetError();
  const queryClient = useQueryClient();

  const { data, error } = useLinearPacingSummary(start, leftKey, mediaTypeDimension);

  useEffect(() => {
    if (error) {
      setError({ message: 'Could not fetch data for pacing detail page', reportError: error });
    }
  }, [error, setError]);

  // Unsaved overrides belong to rows of the previous summary.
  useEffect(() => {
    setSpendOverrides({});
  }, [start, leftKey, mediaTypeDimension]);

  const allLeftRows = useMemo(() => (data ? toPacingRows(data, leftKey) : []), [data, leftKey]);

  const { leftMax, leftRows } = useMemo(() => {
    const filtered = selectedLeft
      ? allLeftRows.filter(row => row.key === selectedLeft)
      : allLeftRows.filter(leftFilter);
    // Largest gap between booked and estimated first.
    filtered.sort(
      (a, b) =>
        Math.abs(b.booked.spend - estimatedSpend(b, acceptedDimension)) -
        Math.abs(a.booked.spend - estimatedSpend(a, acceptedDimension))
    );
    return { leftMax: maxSpend(filtered, acceptedDimension), leftRows: filtered };
  }, [allLeftRows, selectedLeft, leftFilter, acceptedDimension]);

  const rightRows = useMemo(() => {
    const children = allLeftRows.find(row => row.key === selectedLeft)?.[rightKey];
    return Array.isArray(children) ? toPacingRows(children, rightKey) : undefined;
  }, [allLeftRows, selectedLeft, rightKey]);

  const { rightMax, rightRowsFiltered } = useMemo(() => {
    const filtered = selectedRight
      ? (rightRows ?? []).filter(row => row.key === selectedRight)
      : (rightRows ?? []).filter(rightFilter);
    return { rightMax: maxSpend(filtered, acceptedDimension), rightRowsFiltered: filtered };
  }, [rightRows, selectedRight, rightFilter, acceptedDimension]);

  const logsParams = useMemo(() => {
    const leftRow = allLeftRows.find(row => row.key === selectedLeft);
    const rightRow = rightRows?.find(row => row.key === selectedRight);
    if (!leftRow || !rightRow) {
      return undefined;
    }
    const [companyRow, networkRow] =
      leftKey === COMPANY_DIMENSION ? [leftRow, rightRow] : [rightRow, leftRow];
    return {
      week: start,
      company: companyRow.name,
      network: networkRow.name,
      campaign: companyRow.campaign ?? '',
      mediaType: mediaTypeDimension
    };
  }, [allLeftRows, rightRows, selectedLeft, selectedRight, leftKey, start, mediaTypeDimension]);

  const { data: bottomData, error: bottomError } = useCompanyNetworkLogs(logsParams);

  useEffect(() => {
    if (bottomError) {
      setError({
        message: 'Could not fetch detailed data for pacing detail page',
        reportError: bottomError
      });
    }
  }, [bottomError, setError]);

  const renderLogsLoading = useCallback(
    () => (
      <TableLoading
        isLoading={!bottomData && !bottomError}
        error={bottomError}
        emptyMessage='No logs for this selection'
      />
    ),
    [bottomData, bottomError]
  );

  useEffect(() => {
    setFilteredBottomData(bottomData);
  }, [bottomData]);

  const onFilteredBottomDataChange = useCallback((rows?: BottomDataRow[]) => {
    setFilteredBottomData(current => (R.equals(current, rows) ? current : rows));
  }, []);

  const bottomTotalsRow = useMemo(() => {
    if (!filteredBottomData) {
      return {};
    }
    const sum = (getValue: (row: BottomDataRow) => number) =>
      filteredBottomData.reduce((total, row) => total + getValue(row), 0);
    const logSpend = sum(sumSpotCost);
    const bookedSpend = sum(row => row.spend);
    return {
      count: `#${sum(row => row.count)}`,
      spend: `$${bookedSpend}`,
      ...Object.fromEntries(
        DAY_COLUMNS.map((day, index) => [day, `$${sum(row => row.spots[index].cost)}`])
      ),
      logSpend: `$${logSpend}`,
      pct: `%${logSpend / bookedSpend}`
    };
  }, [filteredBottomData]);

  const bottomColHeaders: TypedHeader<BottomDataRow>[] = [
    { label: 'Avail', name: 'avail', width: 70 },
    { label: 'Len', name: 'length', width: 70 },
    { label: 'Days', name: 'dow', flex: 1 },
    {
      label: 'Start',
      name: 'daypart_start',
      flex: 1,
      renderer: row => convert24hrTo12hr(row.daypart_start)
    },
    {
      label: 'End',
      name: 'daypart_end',
      flex: 1,
      renderer: row => convert24hrTo12hr(row.daypart_end)
    },
    {
      label: 'Rotation',
      name: 'rotation',
      flex: 1,
      minFlexWidth: 200,
      renderer: row => row.rotation
    },
    { label: 'Type', name: 'type', renderer: row => TYPES_TO_NAMES[String(row.type)] },
    {
      label: '#',
      name: 'count',
      renderer: row => <NumberFormatter value={row.count} type='#' decimals={0} />,
      width: 50
    },
    {
      label: 'Cost',
      name: 'cost',
      renderer: row => <NumberFormatter value={row.cost} type='$' decimals={0} />
    },
    {
      label: 'Spend',
      name: 'spend',
      renderer: row => <NumberFormatter value={row.spend} type='$' decimals={0} />
    },
    ...DAY_COLUMNS.map(createLogsHeaderRow),
    {
      label: 'Total',
      name: 'logSpend',
      renderer: row => {
        const value = displayedSpend(spendOverrides[row.id], row.spend_override, sumSpotCost(row));
        if (focusedCell === row.id) {
          return (
            <SelfFocusingInput
              onBlur={() => setFocusedCell(undefined)}
              type='number'
              value={value}
              onChange={e => {
                const spendOverride = Math.abs(parseInt(e.target.value, 10) || 0);
                setSpendOverrides(current => ({ ...current, [row.id]: spendOverride }));
              }}
            />
          );
        }
        const cell = (
          <InteractiveDiv
            onClick={() => setFocusedCell(row.id)}
            className={cn({ 'pacing-spend-override': hasSpendOverride(row.spend_override) })}
          >
            <NumberFormatter value={value} type='$' decimals={0} />
          </InteractiveDiv>
        );
        return hasSpendOverride(row.spend_override) ? (
          <OverlayTrigger
            placement={Placement.BOTTOM}
            overlay={<Tooltip id={String(row.id)}>This is an overridden estimate</Tooltip>}
          >
            {cell}
          </OverlayTrigger>
        ) : (
          cell
        );
      }
    },
    {
      label: '%',
      name: 'pct',
      renderer: row =>
        row.spend ? (
          <NumberFormatter value={sumSpotCost(row) / row.spend} type='%' decimals={0} />
        ) : (
          '—'
        ),
      width: 70
    }
  ];

  const saveEdits = async () => {
    setSaving(true);
    try {
      await saveEstimateOverrides(
        Object.entries(spendOverrides).map(([id, spend]) => ({ id: parseInt(id, 10), spend }))
      );
      setSpendOverrides({});
      setSelectedRight(undefined);
      queryClient.removeQueries({ queryKey: [LOGS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SUMMARY_QUERY_KEY] });
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  const renderOverlay = (row: PacingRow) => {
    const spends = adjustedSpends(row, spendDimension, acceptedDimension);
    return (
      <PacingOverlay
        title={row.name}
        reprocessing={row.logs.spend === 0 && spends.booked > 1000}
        logs={spends.logs}
        estimated={spends.estimated}
        booked={spends.booked}
        secured={spends.securedLogs}
        unsecured={spends.unsecuredLogs}
        upfront={spends.upfrontLogs}
        targets={
          row.tvBudget
            ? { budget: spends.budget, assumedClearanceRate: row.assumedClearanceRate }
            : undefined
        }
      />
    );
  };

  const barAriaLabel = (row: PacingRow, spends: ReturnType<typeof adjustedSpends>) =>
    spends.logs > 0
      ? `${row.name} pacing: ${pacingLabel(spends.estimated, spends.booked)}`
      : row.name;

  const renderLeftBar = (row: PacingRow) => {
    const spends = adjustedSpends(row, spendDimension, acceptedDimension);
    const hasLogs = spends.logs > 0;
    return (
      <PacingBar
        max={leftMax}
        layers={hasLogs ? spendLayers(spends) : reprocessingLayers(leftMax)}
        markers={
          hasLogs
            ? [
                {
                  key: 'booked-target',
                  value: spends.booked * row.assumedClearanceRate,
                  color: PacingBarColors.bookedTarget
                },
                ...(spends.budget
                  ? [
                      {
                        key: 'client-target',
                        value: spends.budget,
                        color: PacingBarColors.clientTarget
                      }
                    ]
                  : [])
              ]
            : []
        }
        endLabel={hasLogs && pacingLabel(spends.estimated, spends.booked)}
        alert={row.commission == null && !row.hasPrelogs && NO_COMMISSION_OR_PRELOGS_WARNING}
        overlay={renderOverlay(row)}
        ariaLabel={barAriaLabel(row, spends)}
      />
    );
  };

  const renderRightBar = (row: PacingRow) => {
    const spends = adjustedSpends(row, spendDimension, acceptedDimension);
    const hasLogs = spends.logs > 0;
    return (
      <PacingBar
        max={rightMax}
        layers={hasLogs ? spendLayers(spends) : reprocessingLayers(rightMax)}
        markers={
          hasLogs ? [{ key: 'booked', value: spends.booked, color: PacingBarColors.booked }] : []
        }
        endLabel={hasLogs && pacingLabel(spends.estimated, spends.booked)}
        alert={!row.hasPrelogs && NO_PRELOGS_WARNING}
        overlay={renderOverlay(row)}
        ariaLabel={barAriaLabel(row, spends)}
      />
    );
  };

  if (!data) {
    return (
      <div className='pacing-dashboard'>
        <div className='pacing-panes'>
          <div className='pacing-left-pane'>{error ? <LoadFailedState /> : <RowsSkeleton />}</div>
        </div>
      </div>
    );
  }

  const hasOverrides = Object.keys(spendOverrides).length > 0;
  const selectedRow = allLeftRows.find(row => row.key === selectedLeft);
  const selectedSpends =
    selectedRow && adjustedSpends(selectedRow, spendDimension, acceptedDimension);
  // Shown in whichever pane is open, so pending edits can always be saved.
  const saveControls = hasOverrides && (
    <PendingChangesControls
      hasPendingChanges
      saveChanges={saveEdits}
      isSaving={saving}
      clearAllChanges={() => setSpendOverrides({})}
    />
  );

  return (
    <div className='pacing-dashboard'>
      <div className='pacing-panes'>
        <div className={cn('pacing-left-pane', { condensed: selectedLeft })}>
          <PacingPane
            title={selectedLeft ? undefined : paneTitle(leftKey, leftRows.length)}
            items={leftRows}
            selectedKey={selectedLeft}
            onSelect={key => {
              setSelectedRight(undefined);
              setSelectedLeft(key);
            }}
            controls={
              selectedLeft ? undefined : (
                <>
                  <FilterBar<object>
                    hasAdvanced
                    options={[leftKey]}
                    lines={uniqueLines(allLeftRows, leftKey)}
                    onFilter={filter => setLeftFilter(() => filter)}
                    storageKeySuffix='linear-pacing-campaign-left'
                  />
                  {leftRows.length > 0 && (
                    <CsvDownloadButton
                      filename={`Linear Pacing by campaign for week of ${start}`}
                      rows={() => paneCsvRows(leftRows, leftKey, spendDimension, acceptedDimension)}
                    />
                  )}
                  {saveControls}
                </>
              )
            }
            labelRenderer={row => renderLabel(leftKey, row)}
            lineItemRenderer={selectedLeft ? undefined : renderLeftBar}
          />
        </div>
        {selectedLeft && (
          <div className='pacing-right-pane'>
            <div className={cn('pacing-top-pane', { condensed: selectedRight })}>
              {rightRows ? (
                <PacingPane
                  title={selectedRight ? undefined : paneTitle(rightKey, rightRowsFiltered.length)}
                  kpis={
                    selectedRight || !selectedSpends
                      ? undefined
                      : pacingKpis(
                          selectedSpends.booked,
                          selectedSpends.logs,
                          selectedSpends.estimated
                        )
                  }
                  items={rightRowsFiltered}
                  selectedKey={selectedRight}
                  onSelect={setSelectedRight}
                  controls={
                    selectedRight ? undefined : (
                      <>
                        <FilterBar<object>
                          hasAdvanced
                          options={[rightKey]}
                          lines={uniqueLines(rightRows, rightKey)}
                          onFilter={filter => setRightFilter(() => filter)}
                          storageKeySuffix='linear-pacing-campaign-right'
                        />
                        {rightRowsFiltered.length > 0 && (
                          <CsvDownloadButton
                            filename={`Linear Pacing by campaign for ${selectedLeft} week of ${start}`}
                            rows={() =>
                              paneCsvRows(
                                rightRowsFiltered,
                                rightKey,
                                spendDimension,
                                acceptedDimension
                              )
                            }
                          />
                        )}
                        {saveControls}
                      </>
                    )
                  }
                  labelRenderer={row => renderLabel(rightKey, row)}
                  lineItemRenderer={renderRightBar}
                />
              ) : (
                <RowsSkeleton />
              )}
            </div>
            {selectedRight && (
              <div className='pacing-bottom-pane'>
                {saveControls && (
                  <div className='pacing-pane-controls align-end'>{saveControls}</div>
                )}
                <BPMTable<BottomTableRow, string>
                  alternateColors
                  headers={bottomColHeaders}
                  superHeaders={BOTTOM_SUPER_HEADERS}
                  data={bottomData ?? []}
                  totals={bottomTotalsRow}
                  totalsRenderer={totalsRenderer}
                  noRowsRenderer={renderLogsLoading}
                  onFilteredDataChange={onFilteredBottomDataChange}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
